import type { SystemColorsTypes } from '@Shelly/shelly-design-system/helpers/types';
import type { LabelTypes } from '@Shelly/shelly-design-system/Label/label-helpers';
import { get } from 'svelte/store';
import { showNewLayout } from 'src/stores/toDelete/new_layout';

export type DeviceListLayoutType = 'default' | 'compact';

const LEGACY_COLOR_MAP: Record<LabelTypes, `${SystemColorsTypes}` | '--color-experimental' | null> = {
	default: '--color-base',
	primary: '--color-primary',
	'primary-10': '--color-primary-10',
	secondary: '--color-secondary',
	success: '--color-success',
	'success-10': '--color-success-10',
	warning: '--color-warning',
	'warning-10': '--color-warning-10',
	danger: '--color-danger',
	premium: '--color-premium',
	'premium-10': '--color-premium-10',
	'color-0': '--color-0',
	'color-2': '--color-2',
	ai: '--gradient-ai',
	disabled: null,
	experimental: '--color-experimental',
};

function isSystemColor(value: string): value is SystemColorsTypes | '--color-experimental' {
	return value.startsWith('--color-') || value.startsWith('--gradient-');
}

function isLegacyColorName(value: string): boolean {
	return value.startsWith('color-') || value.startsWith('gradient-') || value.startsWith('chart-');
}

export function mapLegacyColorToToken(
	value?: `${LabelTypes}` | `${SystemColorsTypes}`,
): `${SystemColorsTypes}` | '--color-experimental' {
	if (!value) return undefined;

	if (value in LEGACY_COLOR_MAP) {
		const mapped = LEGACY_COLOR_MAP[value as LabelTypes];
		return mapped ?? undefined; // null => unsupported
	}

	if (isSystemColor(value)) {
		return value;
	}

	if (isLegacyColorName(value)) {
		const token = `--${value}` as `${SystemColorsTypes}`;

		if (isSystemColor(token)) {
			return token;
		}

		return undefined;
	}

	// legacy but unsupported
	return undefined;
}

// All icons in the new LabelV2 should be light instead of solid.
export function mapIconSolidWeightToLight(icon: string) {
	if (!icon) return undefined;

	const splittedIcon = icon.split(' ');

	// replace 'fak' custom icons
	if (splittedIcon[0] === 'fak') {
		return icon
			.replace(/\bfa-sharp-(light|regular|thin|solid)\b/, 'fa-sharp-light')
			.replace(/\bfa-(light|regular|thin|solid)\b/, 'fa-light')
			.replace(/\b(fal|fas|fat|far)-/, 'fa-light-');
	}

	// replace all weight variants with "fa-light"
	return icon?.replace(
		/fa-solid|fa-thin|fa-regular|fa-light|fas(?!\w)|fal(?!\w)|fat(?!\w)|far(?!\w)|fa(?=\s)/g,
		'fa-light',
	);
}

// Symmetric to `mapIconSolidWeightToLight` — force a Font Awesome class string
// to its solid variant regardless of the source weight. Used for the dashboard
// Tab icons which live in the icon picker as `fat` (thin) but must render solid
// inline with the tab title per the new-layout Figma. Kept app-side so we don't
// mutate stored dashboard data or force weight decisions into the design system.
export function mapIconWeightToSolid(icon: string): string | undefined {
	if (!icon) return undefined;

	const splittedIcon = icon.split(' ');

	// 'fak' custom kit icons use a hyphenated weight suffix (`fa-<weight>-...`).
	if (splittedIcon[0] === 'fak') {
		return icon
			.replace(/\bfa-sharp-(light|regular|thin|solid)\b/, 'fa-sharp-solid')
			.replace(/\bfa-(light|regular|thin|solid)\b/, 'fa-solid')
			.replace(/\b(fal|fat|far|fas)-/, 'fa-solid-');
	}

	// Standard Font Awesome families: swap any weight token to solid.
	return icon.replace(
		/fa-solid|fa-thin|fa-regular|fa-light|fas(?!\w)|fal(?!\w)|fat(?!\w)|far(?!\w)|fa(?=\s)/g,
		'fa-solid',
	);
}

// All icons in ParameterCardV2 should be thin (fat) instead of solid (fas).
// According to design guidelines: "The icon font weight in Parameter Cards V2 should always be thin (fat), not solid (fas)"
// Only applies the conversion when showNewLayout is enabled.
export function mapIconToThin(icon: string | undefined): string | undefined {
	if (!icon) return icon;
	// Only convert to thin when new layout is enabled
	if (!get(showNewLayout)) return icon;

	const splittedIcon = icon.split(' ');

	// replace 'fak' custom icons
	if (splittedIcon[0] === 'fak') {
		return icon
			.replace(/\bfa-sharp-(light|regular|thin|solid)\b/, 'fa-sharp-thin')
			.replace(/\bfa-(light|regular|thin|solid)\b/, 'fa-thin')
			.replace(/\b(fal|fas|fat|far)-/, 'fa-thin-');
	}

	// replace all weight variants with "fa-thin"
	return icon?.replace(
		/fa-solid|fa-thin|fa-regular|fa-light|fas(?!\w)|fal(?!\w)|fat(?!\w)|far(?!\w)|fa(?=\s)/g,
		'fa-thin',
	);
}

export function mapOldCustomIconFromDBtoNew(icon: string) {
	if (!icon) return undefined;

	const [prefix, iconClass] = icon.split(' ');

	if (!prefix.includes('ci')) {
		return icon; // already new format or not custom icon
	}

	const prefixMap = {
		cis: 'fa-solid',
		cil: 'fa-light',
		cir: 'fa-regular',
	};

	const variant = prefixMap[prefix];

	const name = iconClass.replace(/^ci-/, '');
	return `fak ${variant}-${name}`;
}
